import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Sparkles as SparklesIcon, Crown } from "lucide-react";
import Sparkles from "../components/Sparkles";
import LangToggle from "../components/LangToggle";
import Avatar3D, { pickPose } from "../components/Avatar3D";
import FireworksBurst from "../components/FireworksBurst";
import RoyalBanquet from "../components/RoyalBanquet";
import { fileUrl, getGallery, getKingdomInfo } from "../lib/api";
import { useLang, t } from "../lib/i18n";
import { relationLabel } from "../lib/relations";

const CASTLE_BG =
  "https://images.unsplash.com/photo-1766191395451-91c79c6ed395?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwzfHxtYWdpY2FsJTIwZmFpcnklMjB0YWxlJTIwY2FzdGxlJTIwcGluayUyMHNreXxlbnwwfHx8fDE3ODE1OTQ1OTd8MA&ixlib=rb-4.1.0&q=85";

const TE = { fontFamily: "'Tiro Telugu','Noto Serif Telugu','Yatra One','Eczar',serif" };

const COSTUMES = {
  father:        { gown: "#1F2D5A", accent: "#D4AF37", icon: "🛡️" },
  mother:        { gown: "#FF7AA8", accent: "#FCF6BA", icon: "👑" },
  grandfather:   { gown: "#3D1F0A", accent: "#FCF6BA", icon: "👑" },
  grandmother:   { gown: "#7A1F4F", accent: "#FCF6BA", icon: "👑" },
  peddananna:    { gown: "#0F1F4C", accent: "#D4AF37", icon: "👑" },
  peddamma:      { gown: "#8B1A5C", accent: "#FCF6BA", icon: "👑" },
  chinnanna:     { gown: "#1B4D7A", accent: "#D4AF37", icon: "⚔️" },
  pinni:         { gown: "#B97AC6", accent: "#FCF6BA", icon: "✨" },
  attha:         { gown: "#C25B7C", accent: "#FCF6BA", icon: "🌸" },
  mamayya:       { gown: "#1F5A3A", accent: "#D4AF37", icon: "⚔️" },
  friend_male:   { gown: "#2C7A7B", accent: "#D4AF37", icon: "💫" },
  friend_female: { gown: "#FF9FB2", accent: "#FCF6BA", icon: "💃" },
};

let _voicesReady = false;
function ensureVoices() {
  if (_voicesReady || !window.speechSynthesis) return;
  const tryLoad = () => {
    const v = window.speechSynthesis.getVoices();
    if (v.length) _voicesReady = true;
  };
  tryLoad();
  window.speechSynthesis.onvoiceschanged = tryLoad;
}

function speak(name, relationText, lang) {
  try {
    if (!window.speechSynthesis) return;
    ensureVoices();
    const text = lang === "te"
      ? `మహారాజా ఆజ్ఞ! రాజ కుటుంబం విచ్చేసారు. ${name} గారు, ${relationText}.`
      : `By royal decree! The Royal Family has arrived. Welcome ${name}, ${relationText}.`;
    const u = new SpeechSynthesisUtterance(text);
    const voices = window.speechSynthesis.getVoices();
    const preferred = voices.find((v) =>
      lang === "te" ? /te|telugu|hi-IN|en-IN/i.test(v.lang) : /en-IN|en-US|en-GB/i.test(v.lang)
    );
    if (preferred) u.voice = preferred;
    u.lang = lang === "te" ? "te-IN" : "en-IN";
    u.rate = 0.95;
    u.pitch = 1.1;
    u.volume = 1;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
  } catch (e) {
    console.error("TTS failed", e);
  }
}

function milestone(n, lang) {
  if (n >= 100) return t("df_milestone_100", lang);
  if (n >= 75)  return t("df_milestone_75", lang);
  if (n >= 50)  return t("df_milestone_50", lang);
  if (n >= 25)  return t("df_milestone_25", lang);
  if (n >= 10)  return t("df_milestone_10", lang);
  return t("df_milestone_0", lang);
}

// Hash a guest id deterministically to a 32-bit unsigned int
function hash(s) {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < (s || "").length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h;
}

/**
 * WanderingDancer — moves along a continuous closed SVG path that covers
 * most of the dance floor (not just orbiting Anagha). Each guest gets a
 * unique meandering loop with random control points (deterministic on id).
 */
function WanderingDancer({ guest, costume, size, durationSec, pathD, startOffset, bobble, pose }) {
  return (
    <div
      className="absolute"
      style={{
        // 0-sized anchor point that travels the path. offset-anchor centers
        // the element on the path point. The inner Avatar3D then translates
        // -50%/-50% from there to render around that point.
        left: 0, top: 0, width: 0, height: 0,
        offsetPath: `path('${pathD}')`,
        offsetRotate: "0deg",
        offsetAnchor: "0 0",
        animation: `wander ${durationSec}s linear infinite`,
        animationDelay: `-${startOffset * durationSec}s`,
      }}
    >
      <div style={{
        animation: `${bobble} ${1.6 + (startOffset * 7) * 0.1}s ease-in-out infinite`,
      }}>
        <Avatar3D guest={guest} costume={costume} size={size} pose={pose} />
      </div>
    </div>
  );
}

/**
 * Build a meandering closed loop path for one dancer. Uses cubic Bezier
 * waypoints across the dance floor; deterministic on guest hash so the
 * same guest always follows the same dance. Waypoints AND control points
 * are clamped so the dancer never leaves the safe area inside the viewport.
 */
function buildPath(seed, W, H, avoid, avatarH, topReserve) {
  // avoid = { cx, cy, r } — center of Anagha to give a buffer
  const rng = (() => {
    let s = seed | 0;
    return () => { s = (s * 1664525 + 1013904223) | 0; return ((s >>> 0) % 1000) / 1000; };
  })();
  // Padding accounts for half the avatar height/width so the figure stays
  // fully on screen as it travels (offset-anchor centers the figure on the path).
  const PAD_X = Math.max(W * 0.06, avatarH * 0.35 + 12);
  const PAD_Y = Math.max(H * 0.08, avatarH * 0.55 + 12);
  const minX = PAD_X, maxX = W - PAD_X;
  // Reserve a zone at the top of the screen for the Royal Banquet banner so
  // wandering avatars never overlap the table tents.
  const minY = Math.max(PAD_Y, (topReserve || 0) + avatarH * 0.55 + 8);
  const maxY = H - PAD_Y;
  const clampX = (v) => Math.min(maxX, Math.max(minX, v));
  const clampY = (v) => Math.min(maxY, Math.max(minY, v));

  const N = 6 + Math.floor(rng() * 3); // 6..8 waypoints
  const pts = [];
  for (let i = 0; i < N; i++) {
    let x, y, tries = 0;
    do {
      x = minX + rng() * (maxX - minX);
      y = minY + rng() * (maxY - minY);
      tries++;
      if (tries > 20) break;
      const dx = x - avoid.cx, dy = y - avoid.cy;
      if (dx * dx + dy * dy >= avoid.r * avoid.r) break;
    } while (true);
    pts.push({ x, y });
  }

  // Tight control points (factor 6) + clamp so Bezier never overshoots
  const cmds = [`M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)}`];
  for (let i = 0; i < N; i++) {
    const p0 = pts[i];
    const p1 = pts[(i + 1) % N];
    const prev = pts[(i - 1 + N) % N];
    const next = pts[(i + 2) % N];
    const c1x = clampX(p0.x + (p1.x - prev.x) / 6);
    const c1y = clampY(p0.y + (p1.y - prev.y) / 6);
    const c2x = clampX(p1.x - (next.x - p0.x) / 6);
    const c2y = clampY(p1.y - (next.y - p0.y) / 6);
    cmds.push(`C ${c1x.toFixed(1)} ${c1y.toFixed(1)}, ${c2x.toFixed(1)} ${c2y.toFixed(1)}, ${p1.x.toFixed(1)} ${p1.y.toFixed(1)}`);
  }
  cmds.push("Z");
  return cmds.join(" ");
}

export default function DanceFloor({ embed }) {
  const lang = useLang();
  const isEmbed = embed === true || (typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("embed") === "1");
  const [guests, setGuests] = useState([]);
  const [info, setInfo] = useState({ princess_name: "Anagha", princess_image: null });
  const [latestId, setLatestId] = useState(null);
  const [floorSize, setFloorSize] = useState({ w: window.innerWidth, h: window.innerHeight });
  const knownIds = useRef(new Set());
  const langRef = useRef(lang);
  useEffect(() => { langRef.current = lang; }, [lang]);

  useEffect(() => {
    const onResize = () => setFloorSize({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Any pointer-down anywhere warms TTS
  useEffect(() => {
    const onAnyClick = () => {
      try {
        const u = new SpeechSynthesisUtterance("\u200b");
        u.volume = 0;
        window.speechSynthesis.speak(u);
      } catch { /* ignore */ }
    };
    window.addEventListener("pointerdown", onAnyClick);
    return () => window.removeEventListener("pointerdown", onAnyClick);
  }, []);

  // Polling: gallery + arrival detection + TTS
  useEffect(() => {
    getKingdomInfo().then(setInfo).catch(() => {});
    const refresh = async () => {
      try {
        const g = await getGallery();
        const newOnes = g.filter(x => !knownIds.current.has(x.id));
        if (knownIds.current.size > 0 && newOnes.length) {
          const arrival = newOnes[0];
          setLatestId(arrival.id);
          speak(arrival.name, relationLabel(arrival.relationship, langRef.current), langRef.current);
          setTimeout(() => setLatestId(null), 5500);
        }
        g.forEach(x => knownIds.current.add(x.id));
        if (knownIds.current.size === 0) g.forEach(x => knownIds.current.add(x.id));
        setGuests(g);
      } catch { /* polling errors ignored */ }
    };
    refresh();
    const tm = setInterval(refresh, 5000);
    return () => clearInterval(tm);
  }, []);


  // Build a wandering path per guest covering the whole dance floor.
  // Princess Anagha sits at the center — give a buffer zone of ~14% so dancers
  // don't visually overlap her at every loop tick.
  const positioned = useMemo(() => {
    const { w: W, h: H } = floorSize;
    const avoid = { cx: W / 2, cy: H / 2, r: Math.min(W, H) * 0.18 };
    const sizes = [Math.min(W, H) * 0.10, Math.min(W, H) * 0.09, Math.min(W, H) * 0.085];
    const bobbles = ["bobbleA", "bobbleB", "bobbleC", "bobbleD", "bobbleE"];
    // Top zone reserved for the Royal Banquet banner. Keep in sync with the
    // banner height in RoyalBanquet.jsx (~200px desktop, ~230px mobile).
    const topReserve = W >= 640 ? 210 : 240;
    return guests.map((g) => {
      const h = hash(g.id || g.name);
      const pose = pickPose(g.id || g.name);
      const duration = 32 + (h % 22); // 32–54s per loop
      const startOffset = ((h >> 6) % 1000) / 1000;
      const size = sizes[h % sizes.length];
      // Avatar height includes the figure (size * 225/116) plus the name label (~24px)
      const avatarH = size * (225 / 116) + 28;
      const pathD = buildPath(h, W, H, avoid, avatarH, topReserve);
      const bobble = bobbles[(h >> 3) % bobbles.length];
      return { guest: g, pose, size, duration, startOffset, pathD, bobble };
    });
  }, [guests, floorSize]);

  const princessSrc = "/anagha-dance.png";

  return (
    <div className="fixed inset-0 overflow-hidden bg-plum-deep text-blush" data-testid="dance-floor">
      <div
        className="absolute inset-0 animate-ken-burns"
        style={{ backgroundImage: `url(${CASTLE_BG})`, backgroundSize: "cover", backgroundPosition: "center" }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-plum-deep/50 via-plum/45 to-plum-deep/90" />
      <Sparkles count={120} />

      {[...Array(8)].map((_, i) => (
        <div
          key={i}
          className="absolute rounded-full"
          style={{
            left: `${10 + i * 11}%`,
            top: `${15 + (i % 3) * 8}%`,
            width: 12,
            height: 12,
            background: "radial-gradient(circle, #FFE5A0, #FF8C5C 60%, transparent 70%)",
            boxShadow: "0 0 24px 6px rgba(255,180,80,0.45)",
            animation: `lantern ${5 + i * 0.3}s ease-in-out ${i * 0.5}s infinite`,
          }}
        />
      ))}

      {!isEmbed && (
      <Link to="/welcome" className="absolute top-5 left-5 z-30 glass rounded-full px-3 py-1.5 flex items-center gap-2 text-plum">
        <ArrowLeft size={14} /> <span className="label-eyebrow" style={TE}>{t("back_short", lang)}</span>
      </Link>
      )}

      {!isEmbed && (
      <div className="absolute top-5 right-5 z-30 flex items-center gap-2">
        <LangToggle />
      </div>
      )}

      {/* Royal Banquet (Dining Hall) banner with rotating table tents */}
      <RoyalBanquet />

      <div className="absolute bottom-5 right-5 z-30 text-right glass-deep rounded-2xl px-4 py-2">
        <p className="font-script text-xl text-blush drop-shadow" style={TE}>{t("df_kingdom", lang)}</p>
        <p className="label-eyebrow text-blush/85" style={TE}>
          {guests.length} {t("df_guests", lang)} · {milestone(guests.length, lang)}
        </p>
      </div>

      {/* Princess Anagha at center */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20 flex flex-col items-center">
        {princessSrc && (
          <div
            className="relative w-44 h-44 sm:w-56 sm:h-56 rounded-full overflow-hidden ring-4 ring-[#FCF6BA] shadow-[0_0_80px_rgba(252,246,186,0.75)] animate-float"
            style={{ animationDuration: "4s" }}
          >
            <img src={princessSrc} alt={info.princess_name} className="w-full h-full object-cover" />
          </div>
        )}
        <div className="mt-3 glass-deep rounded-full px-5 py-1.5">
          <p className="font-script text-3xl text-plum" style={TE}>{t("df_princess", lang)}</p>
        </div>
      </div>

      {/* Dancing guests wandering all over the floor */}
      {positioned.map((p) => (
        <WanderingDancer
          key={p.guest.id}
          guest={p.guest}
          costume={COSTUMES[p.guest.relationship] || COSTUMES.friend_male}
          size={p.size}
          durationSec={p.duration}
          pathD={p.pathD}
          startOffset={p.startOffset}
          bobble={p.bobble}
          pose={p.pose}
        />
      ))}

      <FireworksBurst active={!!latestId} />

      {/* Royal arrival proclamation */}
      {latestId && (() => {
        const g = guests.find(x => x.id === latestId);
        if (!g) return null;
        return (
          <div
            data-testid="royal-arrival-proclamation"
            className="absolute left-1/2 -translate-x-1/2 z-40 animate-[arrivalIn_0.6s_ease-out]"
            style={{ top: "260px" }}
          >
            <div
              className="rounded-3xl px-6 sm:px-8 py-4 sm:py-5 min-w-[280px] sm:min-w-[420px] text-center"
              style={{
                background:
                  "linear-gradient(140deg, rgba(74,22,53,0.96) 0%, rgba(43,10,32,0.98) 55%, rgba(20,6,16,0.99) 100%)",
                border: "1.5px solid rgba(212,175,55,0.6)",
                boxShadow:
                  "0 24px 60px -10px rgba(74,22,53,0.6), 0 0 0 4px rgba(212,175,55,0.12), inset 0 1px 0 rgba(212,175,55,0.4)",
              }}
            >
              <div className="flex items-center justify-center gap-2 text-amber-200/90 text-[0.62rem] tracking-[0.28em] uppercase" style={TE}>
                <Crown size={12} className="text-amber-300" />
                {t("arrival_proclaim", lang)}
                <Crown size={12} className="text-amber-300" />
              </div>
              <p
                className="mt-2 text-2xl sm:text-3xl leading-tight"
                style={{
                  fontFamily: "'Yatra One','Tiro Telugu','Noto Sans Telugu',serif",
                  background: "linear-gradient(180deg,#FFE9A8 0%,#D4AF37 55%,#8A6A1A 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                👑 {t("arrival_royal_family", lang)}
              </p>
              <p className="mt-1 text-lg sm:text-xl text-amber-50" style={TE}>
                <span data-testid="royal-arrival-name">{g.name}</span>
                <span className="text-amber-300/70"> · </span>
                <span className="text-amber-100/90" data-testid="royal-arrival-relation">
                  {relationLabel(g.relationship, lang)}
                </span>
              </p>
            </div>
          </div>
        );
      })()}

      {guests.length === 0 && (
        <div className="absolute inset-0 grid place-items-center text-blush text-center pointer-events-none" style={{ paddingTop: "30vh" }}>
          <div>
            <Crown className="mx-auto text-[#FCF6BA]" size={32} />
            <p className="font-script text-4xl mt-2" style={TE}>{t("df_waiting", lang)}</p>
            <p className="label-eyebrow mt-2 text-blush/80" style={TE}>{t("df_qr_hint", lang)}</p>
          </div>
        </div>
      )}

      <style>{`
        @keyframes wander {
          from { offset-distance: 0%; }
          to   { offset-distance: 100%; }
        }
        @keyframes bobbleA {
          0%,100% { transform: translateY(0) rotate(0deg); }
          50%     { transform: translateY(-10px) rotate(4deg); }
        }
        @keyframes bobbleB {
          0%,100% { transform: translateY(0) rotate(0deg) scale(1); }
          50%     { transform: translateY(-14px) rotate(-5deg) scale(1.04); }
        }
        @keyframes bobbleC {
          0%,100% { transform: rotate(-3deg) translateY(0); }
          50%     { transform: rotate(3deg) translateY(-8px); }
        }
        @keyframes bobbleD {
          0%,100% { transform: scale(1) rotate(0deg); }
          50%     { transform: scale(1.08) rotate(6deg) translateY(-6px); }
        }
        @keyframes bobbleE {
          0%,100% { transform: translateX(-3px) translateY(0); }
          25%     { transform: translateX(3px) translateY(-6px); }
          75%     { transform: translateX(-3px) translateY(-10px); }
        }
        @keyframes lantern {
          0%,100% { transform: translateY(0); opacity: 0.85; }
          50%     { transform: translateY(-12px); opacity: 1; }
        }
        @keyframes arrivalIn {
          from { opacity: 0; transform: translate(-50%, -20px) scale(0.9); }
          to   { opacity: 1; transform: translate(-50%, 0) scale(1); }
        }
      `}</style>
    </div>
  );
}
